import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { useUpdateUser } from '@/hooks/useUserQueries'
import type { UserDetail } from '@/types/admin'
import {
  USER_EDIT_ROLES,
  userEditSchema,
  type UserEditFormData,
} from '@/schemas/userEdit.schema'

type UserEditRole = UserEditFormData['role']

/** A role the form does not offer falls back to "User", the role reported for a plain account. */
function toEditRole(role: string): UserEditRole {
  return (USER_EDIT_ROLES as readonly string[]).includes(role) ? (role as UserEditRole) : 'User'
}

interface UserEditFormProps {
  user: UserDetail
}

export function UserEditForm({ user }: UserEditFormProps) {
  const navigate = useNavigate()
  const updateUser = useUpdateUser()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<UserEditFormData>({
    resolver: zodResolver(userEditSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber ?? '',
      role: toEditRole(user.role),
    },
  })

  const selectedRole = watch('role')

  useEffect(() => {
    setValue('role', toEditRole(user.role))
  }, [user.role, setValue])

  const onSubmit = async (data: UserEditFormData) => {
    try {
      await updateUser.mutateAsync(
        {
          id: user.id,
          data,
        },
        {
          onSuccess: () => {
            navigate(`/admin/users/${user.id}`)
          },
        }
      )
    } catch {
      // Error handled by the mutation's own toast; the form stays put so the fields aren't lost.
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card>
        <CardHeader>
          <CardTitle>Edit User Information</CardTitle>
          <CardDescription>Update user details and permissions</CardDescription>
        </CardHeader>
        {/* Disabled as a group while the save is out. A native `<fieldset disabled>` reaches
            every form-associated descendant, including the role Select's `<button>` trigger —
            not just the plain inputs. */}
        <fieldset disabled={updateUser.isPending}>
        <CardContent className="space-y-6">
          {/* Name */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                First Name <span className="text-destructive-ink">*</span>
              </Label>
              <Input
                id="firstName"
                placeholder="Enter first name"
                {...register('firstName')}
                aria-invalid={!!errors.firstName}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive-ink">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                placeholder="Enter last name"
                {...register('lastName')}
                aria-invalid={!!errors.lastName}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive-ink">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email Address <span className="text-destructive-ink">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="email@example.com"
              {...register('email')}
              aria-invalid={!!errors.email}
            />
            {errors.email && <p className="text-sm text-destructive-ink">{errors.email.message}</p>}
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">
              Phone Number <span className="text-destructive-ink">*</span>
            </Label>
            <Input
              id="phoneNumber"
              type="tel"
              placeholder="0973821233 or +84973821233"
              {...register('phoneNumber')}
              aria-invalid={!!errors.phoneNumber}
            />
            {errors.phoneNumber && (
              <p className="text-sm text-destructive-ink">{errors.phoneNumber.message}</p>
            )}
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role">
              Role <span className="text-destructive-ink">*</span>
            </Label>
            <Select
              value={selectedRole}
              onValueChange={(value) =>
                setValue('role', value as UserEditRole, {
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger id="role">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="User">User (no admin role)</SelectItem>
                <SelectItem value="SuperAdmin">Super Admin</SelectItem>
                <SelectItem value="BillingAdmin">Billing Admin</SelectItem>
                <SelectItem value="SupportAdmin">Support Admin</SelectItem>
              </SelectContent>
            </Select>
            {errors.role && <p className="text-sm text-destructive-ink">{errors.role.message}</p>}
            <p className="text-sm text-muted-foreground">
              {selectedRole === 'User' && 'A customer account with no access to the admin console.'}
              {selectedRole === 'SuperAdmin' &&
                'Full access to all features and settings including user management.'}
              {selectedRole === 'BillingAdmin' &&
                'Access to billing, invoices, payments, and financial reports.'}
              {selectedRole === 'SupportAdmin' &&
                'Access to support tickets, user assistance, and system monitoring.'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/admin/users/${user.id}`)}
              disabled={updateUser.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isDirty || updateUser.isPending}>
              {updateUser.isPending && <Spinner className="mr-2" />}
              {updateUser.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
        </fieldset>
      </Card>
    </form>
  )
}
